import { game, Game as CCGame, sys, profiler, EventTarget, warn, log, KeyCode, input, Input, Button, Canvas, director, Node, screen, Director } from 'cc';
import { DEBUG, DEV, EDITOR, PREVIEW } from 'cc/env';
import Connection from './Connection/Connection';
import NetworkWebSocket from './Connection/Network/NetworkWebSocket';
import I18n from './I18n/I18n';
import Account, { Currency, DisplaySign, Unit } from './Account';
import AudioManager, { AudioConfig } from './AudioManager';
import ErrorHandler, { ErrorEntity } from './ErrorHandler/ErrorHandler';
import ActivityManager from './ActivityManager';
import SceneManager from './SceneManager';
import OrientationManager, { Orientation } from './OrientationManager';
import ShaderManager from './ShaderManager';
import BezierPathManager from './Toolkit/Components/MovingEffect/Bezier/BezierPathManager';
import MessageManager, { MessageType } from './MessageManager';
import SpineManager from './SpineManager';
import toolManager from './Toolkit/Tools/ToolManager';
import EffectStageManager from './System/EffectStageManager';
import SnapshotManager from './Toolkit/Components/Shader/PostProcessing/SnapshotManager';
import BundleAssetProvider from './BundleAssetProvider';
import ErrorName from './ErrorHandler/ErrorName';
import GameLaunchConfigType from './Config/GameLaunchConfigType';
import EnvironmentConfigType, { ConfigParamsMap, defaultConfigParamsMap } from './Config/EnvironmentConfigType';
import GameNameConfigType from './Config/GameNameConfigType';
import audioAssetPathConfig from './Config/AudioAssetPathConfig';
import AssetLoadingConfigType from './Config/AssetLoadingConfigType';
import globalInstance from './System/GlobalInstance';
import SupportLanguage from './I18n/SupportLanguage';
import Cryptor from './Toolkit/Helper/Cryptor';
import MessageDialog from './Toolkit/Components/Dialog/MessageDialog';
import Page from './Toolkit/Components/Page/Page';
import Parameter from './Toolkit/Utils/Parameter';
import BrowserChecker from './Toolkit/Utils/BrowserChecker';
import AssetReleaseManager from './AssetReleaseManager';
import GlobalExport from './System/GlobalExport';
import Delay from './Toolkit/Utils/Delay';
import { chooseColorByTheme, generateRandomString, joinURL } from './Toolkit/Utils/Utility';
import { ActivityEventName } from './Config/ActivityConfigType';

enum GameEvent {
    PRELOAD_START = 'PRELOAD_START',
    PRELOAD_COMPLETE = 'PRELOAD_COMPLETE',
    READY = 'READY',
    USER_INTERACTED = 'USER_INTERACTED',
    MESSAGE_MANAGER_CHANGED = 'MESSAGE_MANAGER_CHANGED',
}

export default abstract class Game {
    public static readonly EVENT_TYPE = GameEvent;
    protected static instance: Game = null;

    protected _config: EnvironmentConfigType = null;
    protected _connection: Connection<any> = null;
    protected _audioManager: AudioManager;
    protected _activityManager: ActivityManager;
    protected _i18n: I18n = null;
    protected _errorHandler: ErrorHandler = null;
    protected _orientationManager: OrientationManager = null;
    protected _shaderManager: ShaderManager = null;
    protected _account: Account = null;
    protected _spineManager: SpineManager = null;
    protected _assetReleaseManager: AssetReleaseManager = null;
    protected _snapshotManager: SnapshotManager = null;
    protected _sceneManager: SceneManager = null;
    protected _messageManager: MessageManager = null;
    protected _bezierPathManager: BezierPathManager = new BezierPathManager(this);
    protected _eventTarget: EventTarget = new EventTarget();
    protected _bundles: { [key: string]: BundleAssetProvider } = {};
    protected _isClickCloseButton = false;
    protected _exports: GlobalExport = null;
    protected _effectStageManager: EffectStageManager = null;
    protected _waitForFirstAckAfterReturn = false;
    private _isReady: boolean = false;
    private _gameNameConfig: GameNameConfigType = null;
    private _isLaunched = false;
    private _launchPromise: Promise<void> = null;
    private _hasUserInteracted = false;
    private _firstUserGesture = true;
    private _firstMeaningfulInteraction = true;
    private _launchPromiseResolve: () => void = null;
    private _firstInteractableButtons: Button[] = [];
    private _canvasNode: Node = null;
    private _frameStart = 0;
    private _frameTime = 0;

    protected constructor(key: string = 'game') {
        this._launchPromise = new Promise<void>((resolve) => {
            this._launchPromiseResolve = resolve;
        });
        globalInstance.registerGame(key, this);
    }

    public static get Instance() {
        if (!this.instance) {
            warn('no game instance has been instantiated');
            return null;
        }
        return this.instance;
    }

    public static get event() {
        return this.Instance._eventTarget;
    }

    public static get config(): Readonly<EnvironmentConfigType> {
        return this.Instance?._config;
    }

    public static get connection() {
        return this.Instance?._connection;
    }

    public static get audio() {
        return this.Instance?._audioManager;
    }

    public static get activity() {
        return this.Instance?._activityManager;
    }

    public static get assetRelease() {
        return this.Instance?._assetReleaseManager;
    }

    public static get i18n() {
        return this.Instance?._i18n;
    }

    public static get account() {
        return this.Instance?._account;
    }

    public static get error() {
        return this.Instance?._errorHandler;
    }

    public static get shader() {
        return this.Instance?.shader;
    }

    public static get spine() {
        return this.Instance?.spine;
    }

    public static get tool() {
        return this.Instance?.tool;
    }

    public static get bundles() {
        return this.Instance?._bundles;
    }

    public static get orientation() {
        return this.Instance?._orientationManager;
    }

    public static get scene() {
        return this.Instance?._sceneManager;
    }
    public static set scene(value: SceneManager) {
        this.Instance._sceneManager = value;
    }

    public static get message() {
        return this.Instance._messageManager;
    }
    public static set message(value: MessageManager) {
        this.Instance.message = value;
    }

    public static get bezierPath() {
        return this.Instance._bezierPathManager;
    }

    public static get snapshot() {
        return this.Instance._snapshotManager;
    }

    public static get exports() {
        return this.Instance.exports;
    }

    public static get canvasNode() {
        return EDITOR || !this.instance ? director.getScene().getComponentInChildren(Canvas).node : this.instance?.canvasNode;
    }

    public static get effectStage(): EffectStageManager {
        return this.Instance?.effectStage;
    }

    public static get frameTime() {
        return this.Instance?._frameTime;
    }

    public get canvasNode() {
        if (!this._canvasNode?.isValid) {
            this._canvasNode = director.getScene().getComponentInChildren(Canvas).node;
        }
        return this._canvasNode;
    }

    public get isReady() {
        return this._isReady;
    }

    public get waitForLaunched() {
        return this._launchPromise;
    }

    public get config() {
        return this._config;
    }

    public get connection() {
        return this._connection;
    }

    public get audio() {
        return this._audioManager;
    }

    public get activity() {
        return this._activityManager;
    }

    public get assetReleaser() {
        return this._assetReleaseManager;
    }

    public get i18n() {
        return this._i18n;
    }

    public get error() {
        return this._errorHandler;
    }

    public get shader() {
        return this._shaderManager;
    }

    public get spine() {
        return this._spineManager;
    }

    public get tool() {
        return toolManager;
    }

    public get bundles() {
        return this._bundles;
    }

    public get orientation() {
        return this._orientationManager;
    }

    public get scene() {
        return this._sceneManager;
    }
    public set scene(value: SceneManager) {
        this._sceneManager = value;
    }

    public get message() {
        return this._messageManager;
    }
    public set message(value: MessageManager) {
        if (this._messageManager === value) {
            return;
        }
        this._messageManager = value;
        if (value) {
            this._listenFatalErrorMessageClose();
        }
        this._eventTarget.emit(GameEvent.MESSAGE_MANAGER_CHANGED, value);
    }

    public get bezierPath() {
        return this._bezierPathManager;
    }

    public get snapshot() {
        return this._snapshotManager;
    }

    public get account() {
        return this._account;
    }

    public get event() {
        return this._eventTarget;
    }

    public get hasUserInteracted() {
        return this._hasUserInteracted;
    }
    private set hasUserInteracted(value: boolean) {
        if (this._hasUserInteracted === value) {
            return;
        }
        this._hasUserInteracted = value;
        this._eventTarget.emit(GameEvent.USER_INTERACTED);
    }

    public get exports() {
        return this._exports;
    }

    public get firstUserGesture() {
        return this._firstUserGesture;
    }
    private set firstUserGesture(value: boolean) {
        if (this._firstUserGesture === value) {
            return;
        }
        this._firstUserGesture = value;
        this.activity.send(ActivityEventName.FirstUserGesture, {});
    }

    public get firstMeaningfulInteraction() {
        return this._firstMeaningfulInteraction;
    }
    private set firstMeaningfulInteraction(value: boolean) {
        if (this._firstMeaningfulInteraction === value) {
            return;
        }
        this._firstMeaningfulInteraction = value;
        this.activity.send(ActivityEventName.FirstMeaningfulInteraction, {});
        this._firstInteractableButtons.forEach((button) => {
            if (button && button.node) {
                button.off(Button.EventType.CLICK, this._firstMeaningfulInteractionCb, this);
            }
        });
        this._firstInteractableButtons.length = 0;
    }

    protected get effectStage(): EffectStageManager {
        if (!this._effectStageManager) {
            this._effectStageManager = new EffectStageManager();
        }
        return this._effectStageManager;
    }

    public startListenFirstInteract() {
        ['pointerup', 'touchend', 'mouseup'].forEach((eventType) => {
            game.canvas.addEventListener(eventType, () => {
                if (this.firstUserGesture) {
                    this.firstUserGesture = false;
                }
            });
        });

        // get all button exist in scene
        const canvas = director.getScene().getComponentInChildren(Canvas);
        this._firstInteractableButtons = canvas.getComponentsInChildren(Button);
        this._firstInteractableButtons.forEach((button) => {
            button.on(Button.EventType.CLICK, this._firstMeaningfulInteractionCb, this);
        });
    }

    public async launch({ gameNameConfig, environmentConfig, errorConfig, audioConfig, assetLoadingConfig }: GameLaunchConfigType) {
        if (this._isLaunched) {
            this._launchPromiseResolve?.();
            throw new Error('Game has been launched!');
        }
        this._isLaunched = true;
        this.initConfig(environmentConfig);
        log(`%c${this._config.gameID} v${this._config.gameVersion}`, `color:${chooseColorByTheme()}`);
        this._gameNameConfig = gameNameConfig;
        this.initFrameTime();

        // init sub modules
        this.initSpineManager();
        this.initAssetReleaser();
        this.initImageQuality();
        const { i18n: i18nAssetLoadingConfig, audio: audioAssetLoadingConfig = audioAssetPathConfig, bundles } = assetLoadingConfig;
        this.initI18n(i18nAssetLoadingConfig);
        this.initBundleAssetProviders(bundles);
        this.initAudioManager(audioAssetLoadingConfig, audioConfig);
        this.initAccount();
        this.initErrorHandler(errorConfig);
        this.initActivityManager();
        this.initGlobalExports?.();
        const { supportRotation, forbidRotation, default: defaultOrientation, orientationScale } = this.config.orientationConfig;
        this.initOrientationManager(supportRotation && !forbidRotation, defaultOrientation, orientationScale);
        this.initShaderManager();
        this.initSnapshotManager();
        await this.retrieveToken(environmentConfig);
        this.initConnection();

        // task after sub modules initialized
        this.initPageShowEvent();
        this._handleGameClosedEvent();
        this._handleBrowserSupported();
        this.handleConnectionError();
        this.handleGlobalError();
        this.handleDocumentTitle();
        this._toggleProfilerStats();
        this.initActivityEvent();
        this._addLanguageDebugKey();
        this._checkIfUserHasInteracted();
        this.afterGameLaunched();
        this._launchPromiseResolve?.();
    }

    public ready() {
        this._isReady = true;
        this._eventTarget.emit(GameEvent.READY);
    }

    public close() {
        if (!this._isClickCloseButton) {
            this._activityManager.send(ActivityEventName.CloseByUI, {});
            this._isClickCloseButton = true;
        }
        if (this._config.useIFrame) {
            window.parent.postMessage({ event: 'close' }, '*');
        } else if (this._config.homeURL) {
            if (window.parent === window) {
                document.location.href = this._config.homeURL;
            } else {
                window.open(this._config.homeURL, '_top');
            }
        } else {
            window.close();
            window.history.back();
        }
    }

    protected initConfig(environmentConfig: EnvironmentConfigType, configParamsMap: ConfigParamsMap = defaultConfigParamsMap) {
        const config = { ...environmentConfig };
        config.hideList = Parameter.get(configParamsMap.hideList, '');
        const hideArray = config.hideList.split(',');
        this._config = config;
        config.gameID = Parameter.get(configParamsMap.gameID, environmentConfig.gameID);
        config.gameDomain = Parameter.get(configParamsMap.gameDomain, environmentConfig.gameDomain);
        config.bonusDomain = Parameter.get(configParamsMap.bonusDomain, environmentConfig.bonusDomain);
        config.isFakeToken = !Parameter.get(configParamsMap.token);
        config.token = Parameter.get(configParamsMap.token, environmentConfig.token) || generateRandomString(36);
        config.platformID = +(Parameter.get(configParamsMap.platformID) ?? environmentConfig.platformID);
        config.walletType = +(Parameter.get(configParamsMap.walletType) ?? environmentConfig.walletType);
        config.currency = Parameter.get(configParamsMap.currency, config.currency);
        config.language = Parameter.get(configParamsMap.language, environmentConfig.language);
        config.homeURL = Parameter.get(configParamsMap.homeURL, environmentConfig.homeURL);
        config.showFPS = Parameter.get(configParamsMap.showFPS, `${!environmentConfig.showFPS}`) === 'false';
        config.useIFrame = Parameter.get(configParamsMap.useIFrame, `${config.useIFrame}`) === 'true';
        config.allowRotationOnPC = Parameter.get(configParamsMap.allowRotationOnPC, `${config.allowRotationOnPC}`) === 'true';
        this.deriveOrientationConfig(config, configParamsMap);
        config.hideTxID = hideArray.includes(configParamsMap.hideTxID);
        config.hideExit = hideArray.includes(configParamsMap.hideExit);
        config.hideZero = hideArray.includes(configParamsMap.hideZero);
        config.hideCurrency = hideArray.includes(configParamsMap.hideCurrency);
        config.hideVersion = hideArray.includes(configParamsMap.hideVersion);
        config.debugKey = DEV && config.debug && Parameter.get(configParamsMap.debugKey, `${config.debugKey}`) === 'true';
        config.enableFullScreen = !(Parameter.get(configParamsMap.enableFullScreen, `${config.enableFullScreen}`) === 'false');
        config.supportCRM = Parameter.get(configParamsMap.supportCRM, `${environmentConfig.supportCRM}`) === 'true';
        config.crm = config.supportCRM && Parameter.get(configParamsMap.crm, `${environmentConfig.crm}`) === 'true';
        config.waitForCRM = Parameter.get(configParamsMap.waitForCRM, `${environmentConfig.waitForCRM}`) === 'true';
        const forceMaxDecimalDigits = Parameter.get(configParamsMap.forceMaxDecimalDigits);
        config.forceMaxDecimalDigits = forceMaxDecimalDigits === null || forceMaxDecimalDigits === '' ? null : +forceMaxDecimalDigits;
        config.numberFormatterMode = +(Parameter.get(configParamsMap.numberFormatterMode) ?? environmentConfig.numberFormatterMode);
        config.displayTimeZone = Parameter.get(configParamsMap.displayTimeZone, `${environmentConfig.displayTimeZone ?? false}`) === 'true';
        config.imageQuality = +(Parameter.get(configParamsMap.imageQuality) ?? environmentConfig.imageQuality);
        config.timezoneOffset = Parameter.get(configParamsMap.timezoneOffset);
        if (config.timezoneOffset) {
            // ensure valid timezone offset format (+/-HH:mm), if input is +H:m, convert it to +0H:0m
            const match = config.timezoneOffset.match(/^([+-])(\d{1,2}):(\d{1,2})$/);
            if (match) {
                const sign = match[1];
                const hours = match[2].padStart(2, '0');
                const minutes = match[3].padStart(2, '0');
                config.timezoneOffset = `${sign}${hours}:${minutes}`;
            } else {
                warn(`Invalid timezone offset format: ${config.timezoneOffset}. Expected format is +HH:mm or -HH:mm.`);
                config.timezoneOffset = null;
            }
        }
    }

    protected initConnection(): void {
        const { gameDomain, gameID, connectionTimeoutDuration } = this._config;
        const serverAddress = joinURL(gameDomain, gameID);
        const cryptor = new Cryptor(gameID, 16);
        const network = new NetworkWebSocket(serverAddress, cryptor);
        this._connection = new Connection(network, connectionTimeoutDuration);
        const originSend = this._connection.send;
        let isFirstSend = true;
        const getToken = () => this._config.token;
        this._connection.send = function send(...args: any) {
            const result = originSend.call(this, ...args);
            if (isFirstSend) {
                isFirstSend = false;
                cryptor.key = getToken();
            }
            return result;
        };
        this._connection.on(Connection.EVENT_TYPE.START_RECONNECT, () => {
            isFirstSend = true;
            cryptor.key = gameID;
        });
    }

    protected initAudioManager(assetLoadingConfig: AssetLoadingConfigType, audioClipConfig: AudioConfig): void {
        this._audioManager = new AudioManager(assetLoadingConfig, audioClipConfig);
    }

    protected initActivityManager(): void {
        this._activityManager = new ActivityManager();
    }

    protected initSpineManager() {
        this._spineManager = new SpineManager();
    }

    protected initAssetReleaser() {
        this._assetReleaseManager = new AssetReleaseManager();
    }

    protected initI18n(config: AssetLoadingConfigType): void {
        this._i18n = new I18n(this._config.language, config);
    }

    protected initAccount() {
        this._account = new Account(Unit.Dollar, Currency.USD, DisplaySign.UnitSign);
    }

    protected initBundleAssetProviders(bundleLoadingConfigs: AssetLoadingConfigType[]) {
        bundleLoadingConfigs?.forEach((bundleLoadingConfig) => {
            this._bundles[bundleLoadingConfig.bundleName] = new BundleAssetProvider(bundleLoadingConfig);
        });
    }

    protected initErrorHandler(config: ErrorEntity[]) {
        this._errorHandler = new ErrorHandler(this, config);
    }

    protected initOrientationManager(supportRotation: boolean, defaultOrientation?: Orientation, orientationScale?: number) {
        this._orientationManager = new OrientationManager(supportRotation, defaultOrientation, undefined, orientationScale);
    }

    protected initShaderManager() {
        this._shaderManager = new ShaderManager();
    }

    protected initSnapshotManager() {
        this._snapshotManager = new SnapshotManager();
    }

    protected async retrieveToken(config: EnvironmentConfigType, configParamsMap: ConfigParamsMap = defaultConfigParamsMap) {
        if (DEBUG) {
            const tID = Parameter.get('tID');
            if (tID) {
                const body = this.getRetrieveTokenBody(config, tID);
                const url = await fetch(`http://192.168.127.155:9093/api/v1/website/launch-game?Platform=${body.Platform}`, {
                    headers: {
                        accept: '*/*',
                        'accept-language': 'zh-TW,zh;q=0.9,en-US;q=0.8,en;q=0.7,ja;q=0.6',
                        'cache-control': 'no-cache',
                        'content-type': 'application/json',
                        pragma: 'no-cache',
                    },
                    referrer: 'http://192.168.127.155:9093/api/v1/pages/login.html',
                    referrerPolicy: 'strict-origin-when-cross-origin',
                    body: JSON.stringify(body),
                    method: 'POST',
                    mode: 'cors',
                    credentials: 'omit',
                })
                    .then((res) => res.json())
                    .then((res) => res.URL);
                const urlObject = new URL(url);
                this.config.platformID = +urlObject.searchParams.get(configParamsMap.platformID);
                this.config.walletType = +urlObject.searchParams.get(configParamsMap.walletType);
                this.config.token = urlObject.searchParams.get(configParamsMap.token);
                this.config.isFakeToken = false;
            }
        }
    }

    protected getRetrieveTokenBody(config: EnvironmentConfigType, tID: string) {
        const tAgent = Parameter.get('tAgent', 'test_twd');
        const tCur = Parameter.get('tCur', 'TWD');
        const tPlatform = Parameter.get('tPlatform', 'dummy');
        const tAmount = +Parameter.get('tAmount', '1000000');
        const tGameID = this.getRetrieveTokenGameID();
        return {
            Version: 'v2',
            Platform: tPlatform,
            Agent: tAgent,
            Balance: tAmount,
            Currency: tCur,
            GameID: tGameID,
            HallName: '',
            HomeURL: '',
            Language: 'en',
            LoginHall: false,
            PlayerAccount: tID,
            PlayerType: 1,
            MinBet: 0,
            MaxBet: 0,
        };
    }

    protected getRetrieveTokenGameID() {
        return this.config.gameID;
    }

    protected enableHeartbeatPacket(loginPath: string, ackPath: string) {
        this._connection.on(loginPath, () => {
            this._connection.startHeartBeating(ackPath);
        });
        this._connection.on(ackPath, () => {
            this._waitForFirstAckAfterReturn = false;
        });
        game.on(CCGame.EVENT_SHOW, () => {
            this._waitForFirstAckAfterReturn = true;
        });
        this._connection.on('disconnect', () => {
            this._connection.stopHeartBeating();
        });
        Delay.timeout(60 * 1000).then(() => {
            this.activity.send(ActivityEventName.NetworkLatency, { latency: this._connection.delay });
        });
    }

    protected handleDocumentTitle() {
        this._i18n.on(I18n.EVENT_TYPE.LanguageChanged, () => {
            document.title = this._gameNameConfig[this._i18n.currentLanguage];
        });
        document.title = this._gameNameConfig[this._i18n.initLanguage];
    }

    protected handleConnectionError() {
        // handle connetion error
        this._connection?.on(Connection.EVENT_TYPE.ERROR, (err, subject, data, path) => {
            if (sys.os === sys.OS.IOS && this._waitForFirstAckAfterReturn && err === ErrorName.CONNECTION_FAILED) {
                err = ErrorName.CONNECTION_FAILED_IOS;
            }
            this._errorHandler.handle(err, { Data: data, Path: path } as any);
        });
        this._connection?.on(Connection.EVENT_TYPE.DISCONNECTED, () => {
            if (this._connection.isReconnecting) {
                log('[Connection] Disconnected while reconnecting; suppressing DISCONNECTED error handling.');
                return;
            }
            this._errorHandler.handle(ErrorName.CONNECTION_FAILED);
        });
    }

    protected handleGlobalError() {
        if (PREVIEW) {
            // do not handle global error in preview mode for debugging
            return;
        }
        const parseErrorTitle: (error: ErrorEvent | PromiseRejectionEvent) => string = (error: ErrorEvent | PromiseRejectionEvent) =>
            error instanceof ErrorEvent ? error.message.split(':')[0] : error.reason?.name;
        const parseErrorStack: (error: ErrorEvent | PromiseRejectionEvent) => string = (error: ErrorEvent | PromiseRejectionEvent) => {
            let stackString = '';
            if (error instanceof ErrorEvent) {
                stackString = error.error.stack;
            } else {
                stackString = error.reason.stack;
            }
            return stackString;
        };
        const checkNeedPassError = (error: ErrorEvent | PromiseRejectionEvent) => {
            const errorTitle = parseErrorTitle(error);
            if (errorTitle.startsWith('NotAllowedError')) {
                return true;
            }
            const stack = parseErrorStack(error);
            if (!stack) {
                return true;
            }
            const url = new URL(window.location.href);
            if (!stack.includes(url.origin)) {
                return true;
            }
            return false;
        };
        window.addEventListener('error', (event) => {
            if (checkNeedPassError(event)) {
                this._errorHandler.reportError(ErrorName.REPORT_ERROR_TO_ACTIVITY, event);
                return;
            }
            this._errorHandler.handle(ErrorName.FRONT_END_UNKNOWN_ERROR, event);
        });
        window.addEventListener('unhandledrejection', (event) => {
            if (checkNeedPassError(event)) {
                this._errorHandler.reportError(ErrorName.REPORT_ERROR_TO_ACTIVITY, event);
                return;
            }
            this._errorHandler.handle(ErrorName.FRONT_END_UNKNOWN_ERROR, event);
        });
    }

    protected initPageShowEvent() {
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                // 避免上一頁後回來玩到一半的狀態, 但實際上已經斷線
                window.location.reload();
            }
            this._activityManager.send(ActivityEventName.PageShow, {});
        });
        window.addEventListener('pagehide', () => {
            this._activityManager.send(ActivityEventName.PageHide, {});
        });
    }

    protected initActivityEvent() {
        this._activityManager.send(ActivityEventName.Config, {
            token: this._config.token,
            gameID: this._config.gameID,
        });
        this._activityManager.send(ActivityEventName.Entry, {
            GameVersion: this._config.gameVersion,
            GameLanguage: this._config.language,
            SupportWebP: sys.hasFeature(sys.Feature.WEBP),
            SupportAVIF: sys.hasFeature(sys.Feature.AVIF),
            ParentHref: (() => {
                try {
                    if (window.top.location.href) {
                        return window.top.location.href;
                    }
                } catch (err) {
                    return window.document.referrer;
                }
                return window.document.referrer;
            })(),
        });
    }

    protected initImageQuality() {
        const { imageQuality } = this._config;
        if (imageQuality !== 0) {
            screen.maxWindowSideLength = imageQuality;
        }
    }

    protected initFrameTime() {
        director.on(Director.EVENT_BEFORE_UPDATE, this._beforeUpdate, this);
        director.on(Director.EVENT_AFTER_UPDATE, this._afterUpdate, this);
        director.on(Director.EVENT_AFTER_DRAW, this._afterDraw, this);
    }

    protected deriveOrientationConfig(config: EnvironmentConfigType, configParamsMap: ConfigParamsMap) {
        const { supportRotation, default: defaultOrientation } = config.orientationConfig;
        let forbidAutoRotation = Parameter.get(configParamsMap.forbidRotation, `${config.orientationConfig.forbidRotation ?? false}`) === 'true';

        const allowManualRotationParam = Parameter.get(configParamsMap.allowManualRotation);
        const allowManualRotation =
            allowManualRotationParam === null ? config.orientationConfig.allowManualRotation ?? false : allowManualRotationParam === 'true';
        // check if orientation configuration is conflicting
        if (!forbidAutoRotation && allowManualRotation) {
            warn('Orientation configuration conflict: auto rotation and manual rotation cannot both be enabled.');
            forbidAutoRotation = true; // resolve conflict by prioritizing allowManualRotation
        }
        if (allowManualRotationParam !== null && !allowManualRotation) {
            // Enable rotation on all devices
            forbidAutoRotation = false;
            config.allowRotationOnPC = true;
        }

        const autoDefaultOrientation = Parameter.get(configParamsMap.autoDefaultOrientation, `${config.orientationConfig.autoDefault ?? false}`) === 'true';
        const orientationScale = config.orientationConfig.orientationScale ?? 1;
        const newDefaultOrientation =
            Parameter.get(configParamsMap.defaultOrientation, `${defaultOrientation}`).toLocaleLowerCase() === Orientation.Landscape.toLocaleLowerCase()
                ? Orientation.Landscape
                : Orientation.Portrait;
        config.orientationConfig = {
            // 遊戲不支援旋轉時，不允許透過參數改變預設方向
            default: supportRotation ? newDefaultOrientation : defaultOrientation,
            autoDefault: autoDefaultOrientation,
            supportRotation,
            forbidRotation: forbidAutoRotation,
            allowManualRotation,
            orientationScale,
        };
    }

    private _handleGameClosedEvent() {
        window.addEventListener('beforeunload', () => {
            // check if close by other type
            if (!this._isClickCloseButton) {
                this._activityManager.send(ActivityEventName.CloseByBrowser, {});
            }
        });
    }

    private _handleBrowserSupported() {
        const { checkBrowser } = this._config;
        if (!checkBrowser) {
            return;
        }
        const info = BrowserChecker.check(checkBrowser);
        if (!info.isSupported) {
            this._errorHandler.handle(ErrorName.BROWSER_NOT_SUPPORTED);
        }
    }

    private _listenFatalErrorMessageClose() {
        this._messageManager.on(MessageManager.EVENT_TYPE.MESSAGE_SHOW, async (type: MessageType, dialog: MessageDialog) => {
            if (type === MessageType.ERROR) {
                await dialog.getComponent(Page).waitForClose();
                this._activityManager.send(ActivityEventName.CloseByError, { ErrorCode: +this._messageManager.lastFatalError.code });
                this._isClickCloseButton = true;
                this.close();
            }
        });
    }

    private _toggleProfilerStats() {
        if (!profiler) {
            return;
        }
        if (this._config.showFPS) {
            profiler.showStats();
        } else {
            profiler.hideStats();
        }
    }

    private _addLanguageDebugKey(): void {
        if (!this._config.debug) {
            return;
        }
        const languageKeyCodeMap: { [keyCode in KeyCode]?: SupportLanguage } = {
            [KeyCode.F1]: SupportLanguage.ENG,
            [KeyCode.F2]: SupportLanguage.SCH,
            [KeyCode.F3]: SupportLanguage.TCH,
            [KeyCode.F4]: SupportLanguage.JP,
            [KeyCode.F5]: SupportLanguage.KR,
            [KeyCode.F6]: SupportLanguage.ID,
            [KeyCode.F7]: SupportLanguage.PT,
            [KeyCode.F8]: SupportLanguage.ES,
            [KeyCode.F9]: SupportLanguage.VI,
            [KeyCode.F10]: SupportLanguage.TH,
            [KeyCode.F11]: SupportLanguage.MS,
            [KeyCode.F12]: SupportLanguage.FIL,
        };
        input.on(Input.EventType.KEY_UP, (event) => {
            const { keyCode } = event;
            const targetLanguage = languageKeyCodeMap[keyCode];
            if (targetLanguage) {
                this._i18n.changeLanguage(targetLanguage);
            }
        });
    }

    private _checkIfUserHasInteracted() {
        ['pointerup', 'touchend', 'mouseup'].forEach((eventType) => {
            game.canvas.addEventListener(eventType, () => {
                this.hasUserInteracted = true;
            });
        });
    }

    private _firstMeaningfulInteractionCb() {
        if (this.firstMeaningfulInteraction) {
            this.firstMeaningfulInteraction = false;
        }
    }

    private _beforeUpdate() {
        this._frameStart = performance.now();
    }

    private _afterUpdate() {
        if (director.isPaused()) {
            this._frameStart = performance.now();
        }
    }

    private _afterDraw() {
        this._frameTime = performance.now() - this._frameStart;
    }

    protected abstract afterGameLaunched(): void;
    protected abstract initGlobalExports(): void;
}
